import cn from 'classnames';
import { FormikProps } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ADDRESS1, ADDRESS2, CITY, COMPANY_NAME, COUNTRY, STATE, TAX_NUMBER, ZIP } from '../../../constants/shipping';
import { selectTaxRequiredCountries } from '../../../store/selectors/shell';
import { IFormFieldConfig, ISelectorValue } from '../../../types/shell';
import { ICommonAddress } from '../../../types/shipping';
import { getTaxFieldConfig } from '../../../utils/helpers';
import { CountrySelector } from '../../index';
import { Input } from '../inputs';

import styles from './FixedAddressForm.module.scss';

interface IProps {
  form: FormikProps<ICommonAddress>;
  requiredFields: Record<string, boolean>;
  className?: string;
  config?: { [key in keyof ICommonAddress]: IFormFieldConfig };
  hideTaxField?: boolean;
}

const FixedAddressForm: React.FC<IProps> = ({ form, requiredFields, className, hideTaxField }) => {
  const taxRequiredCountries = useSelector(selectTaxRequiredCountries);
  const isTaxCodeShown = useMemo(() => !hideTaxField, [hideTaxField]);

  const getFieldErrors = useCallback(
    (fieldName: keyof ICommonAddress) => {
      return form.errors[fieldName];
    },
    [form.errors],
  );

  const handleFormFieldChange = useCallback(
    async (name: keyof ICommonAddress, value: string) => {
      await form.setFieldValue(name, value);
    },
    [form.setFieldValue],
  );

  const handleSelectInputChange = useCallback(
    async (name: keyof ICommonAddress, selected: ISelectorValue) => {
      const { value } = selected || {};
      await form.setFieldValue(name, value, false);
    },
    [form.setFieldValue],
  );

  const handleAddress1Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(ADDRESS1, e.target.value),
    [handleFormFieldChange],
  );

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(ADDRESS2, e.target.value),
    [handleFormFieldChange],
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(CITY, e.target.value),
    [handleFormFieldChange],
  );

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(STATE, e.target.value),
    [handleFormFieldChange],
  );

  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(ZIP, e.target.value),
    [handleFormFieldChange],
  );

  const handleTaxNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFormFieldChange(TAX_NUMBER, e.target.value),
    [handleFormFieldChange],
  );

  const handleCompanyName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      handleFormFieldChange(COMPANY_NAME, e.target.value),
    [handleFormFieldChange],
  );

  const taxFieldConfig = useMemo(
    () => getTaxFieldConfig(form.values.country, taxRequiredCountries),
    [form.values.country, taxRequiredCountries],
  );

  const { country, tax_number } = form.values;

  React.useEffect(() => {
    const taxField = getTaxFieldConfig(country, taxRequiredCountries);

    if ((!taxField?.label || !isTaxCodeShown) && tax_number) {
      form.setFieldValue(TAX_NUMBER, '');
    }
  }, [country, taxRequiredCountries, isTaxCodeShown, tax_number]);

  return (
    <div role="form" className={cn(styles.shippingForm, className)}>
      <div className={styles.info}>
        <div className={styles.inputWrapper}>
          <Input
            helperText="Company name"
            name={COMPANY_NAME}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="Company name"
            value={form.values[COMPANY_NAME] ?? ''}
            onChange={handleCompanyName}
          />
          <Input
            helperText="Address 1"
            name={ADDRESS1}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="Address 1"
            required={requiredFields[ADDRESS1]}
            value={form.values[ADDRESS1] ?? ''}
            onChange={handleAddress1Change}
            error={getFieldErrors(ADDRESS1)}
          />
          <Input
            helperText="Address 2"
            name={ADDRESS2}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="Address 2"
            required={requiredFields[ADDRESS2]}
            value={form.values[ADDRESS2] ?? ''}
            onChange={handleAddress2Change}
            error={getFieldErrors(ADDRESS2)}
          />
          <Input
            helperText="City"
            name={CITY}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="City"
            required={requiredFields[CITY]}
            value={form.values[CITY] ?? ''}
            onChange={handleCityChange}
            error={getFieldErrors(CITY)}
          />
          <Input
            helperText="State"
            name={STATE}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="State"
            required={requiredFields[STATE]}
            value={form.values[STATE] ?? ''}
            onChange={handleStateChange}
            error={getFieldErrors(STATE)}
          />
          <Input
            helperText="ZIP code"
            name={ZIP}
            className={styles.input}
            inputClassName={styles.inputField}
            placeholder="ZIP code"
            required={requiredFields[ZIP]}
            value={form.values[ZIP] ?? ''}
            onChange={handleZipChange}
            error={getFieldErrors(ZIP)}
          />
          <CountrySelector
            containerClassName={cn(styles.input, styles.countrySelectContainer)}
            className={cn(
              { [styles.errorBorder]: form.errors[COUNTRY] },
              styles.inputField,
              styles.fullWidth,
              styles.countrySelect,
            )}
            label="Country"
            name={COUNTRY}
            placeholder="Country"
            required={requiredFields[COUNTRY]}
            value={form.values[COUNTRY] ?? ''}
            onChange={handleSelectInputChange}
            error={getFieldErrors(COUNTRY)}
          />
          {taxFieldConfig?.label && isTaxCodeShown && (
            <Input
              field={TAX_NUMBER}
              className={styles.input}
              inputClassName={styles.inputField}
              helperText={taxFieldConfig.label}
              placeholder={taxFieldConfig.label}
              required={false}
              value={form.values[TAX_NUMBER]}
              onChange={handleTaxNumberChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FixedAddressForm;
